import { useState, useMemo } from 'react';
import {
  Box, Typography, Chip, Switch,
  CircularProgress,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, Send, Visibility, VisibilityOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  alertChannelsService, AlertChannel, EmailChannelConfig, TelegramChannelConfig,
} from '../../services/alert-channels.service';
import { useModalState } from '../../hooks/useModalState';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';

const TYPE_LABELS: Record<string, string> = {
  email: 'Email',
  slack: 'Slack',
  telegram: 'Telegram',
};

function destinationSummary(row: AlertChannel): string {
  if (row.type === 'email') {
    const count = (row.config as EmailChannelConfig)?.recipients?.length ?? 0;
    return `${count} recipient${count === 1 ? '' : 's'}`;
  }
  if (row.type === 'slack') return 'Webhook';
  if (row.type === 'telegram') {
    const chatId = (row.config as TelegramChannelConfig)?.chat_id;
    return chatId ? `Chat ${chatId}` : 'Telegram';
  }
  return '';
}

export default function AlertChannelsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('alert-channel');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);

  const debouncedSearch = useDebounced(search);
  const where = useMemo(
    () => buildEntityWhere('alert_channel', { search: debouncedSearch, statuses, tagIds: [] }, ['name']),
    [debouncedSearch, statuses],
  );
  const { data, isLoading } = useQuery({
    queryKey: ['alert-channels', where],
    queryFn: () => alertChannelsService.query({ where }),
  });

  const rows = useMemo(() => data?.data || [], [data]);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const bulk = async (fn: (id: string) => Promise<unknown>) => {
    await Promise.all(selectedIds.map(fn));
    queryClient.invalidateQueries({ queryKey: ['alert-channels'] });
    setSelection(emptySelection);
  };

  const testMutation = useMutation({
    mutationFn: (id: string) => alertChannelsService.test(id),
    onSuccess: (result) => {
      if (result.ok) {
        enqueueSnackbar('Test message sent successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(`Test failed: ${result.error || 'Unknown error'}`, { variant: 'error' });
      }
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Test failed: ${err.message}`, { variant: 'error' });
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => alertChannelsService.update(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-channels'] });
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Update failed: ${err.message}`, { variant: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'type', headerName: 'Type', width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={TYPE_LABELS[p.value] || p.value} size="small" variant="outlined" />
        </Box>
      ),
    },
    {
      field: 'destination', headerName: 'Destination', width: 160, sortable: false,
      valueGetter: (_value: unknown, row: AlertChannel) => destinationSummary(row),
    },
    {
      field: 'enabled', headerName: 'Enabled', width: 90,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Switch
            size="small"
            checked={!!p.value}
            disabled={toggleEnabledMutation.isPending}
            onChange={(e) => toggleEnabledMutation.mutate({ id: p.row.id, enabled: e.target.checked })}
          />
        </Box>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={p.value} size="small" color={p.value === 'active' ? 'success' : 'warning'} />
        </Box>
      ),
    },
    {
      field: 'created_at', headerName: 'Created', width: 160,
      valueFormatter: (v: string) => v ? new Date(v).toLocaleString() : '',
    },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton
            size="small"
            title="Send test"
            disabled={testMutation.isPending}
            onClick={() => testMutation.mutate(params.row.id)}
          >
            {testMutation.isPending && testMutation.variables === params.row.id
              ? <CircularProgress size={16} />
              : <Send sx={{ fontSize: 16 }} />}
          </TooltipIconButton>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          {params.row.status === 'archived' ? (
            <TooltipIconButton
              size="small"
              title="Unarchive"
              onClick={async () => {
                await alertChannelsService.unarchive(params.row.id);
                queryClient.invalidateQueries({ queryKey: ['alert-channels'] });
              }}
            >
              <Visibility sx={{ fontSize: 16 }} />
            </TooltipIconButton>
          ) : (
            <TooltipIconButton
              size="small"
              color="warning"
              title="Archive"
              onClick={async () => {
                await alertChannelsService.delete(params.row.id);
                queryClient.invalidateQueries({ queryKey: ['alert-channels'] });
              }}
            >
              <VisibilityOff sx={{ fontSize: 16 }} />
            </TooltipIconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Alert Channels</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[50, 100, 200]}
        autoHeight
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Add channel', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'channels',
          selectionActions: [
            { icon: VisibilityOff, hint: 'Archive', onClick: () => bulk((id) => alertChannelsService.delete(id)), color: 'inherit' as const },
            { icon: Visibility, hint: 'Unarchive', onClick: () => bulk((id) => alertChannelsService.unarchive(id)), color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />
    </Box>
  );
}
